import endpoint from '@/util/endpoint';
import { UiProduct } from '@whiz-cart/node-shared/models/product/uiProduct';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import { createCache, createStore } from 'cross-state';

export type StoreProduct = Partial<UiProduct & { unknown?: boolean; imageUrl?: string; mhdGroup?: string }>;

export const productCache = createCache(
    async (storeGuid: string, gtin: string): Promise<StoreProduct> => {
        const response = await endpoint('storeManager.getProductByBarcode', { storeGuid, barcode: gtin }).get<UiProduct | undefined>();
        if (response) return response;

        const unknown = await endpoint('storeManager.getUnknownProductByBarcode', { storeGuid, barcode: gtin }).get<UiProduct>();
        return { ...unknown, gtin, unknown: true };
    },
    { invalidateAfter: (state) => (state.status === 'error' ? { seconds: 10 } : { minutes: 10 }) },
);

export const uploadErrorStore = createStore<string | false>(false);

export function useProduct(gtin: string | undefined) {
    const storeGuid = urlService.state.useStore((url) => url.params.storeGuid);
    const [product] = productCache(storeGuid ?? '', gtin ?? '').useCache({ disabled: !gtin || !storeGuid });
    return product;
}

export function updateProduct(storeGuid: string, ...products: StoreProduct[]) {
    for (const product of products) {
        productCache(storeGuid, product.gtin!).updateValue((current) => ({ ...current, ...product }));
    }
}

export async function postProductImage({ gtin, formData, rotation }: { gtin: string; formData: FormData; rotation: number }) {
    try {
        const storeGuid = urlService.state.get().params.storeGuid ?? '';
        // images uploaded with this endpoint appear to only affect MHD/BBD page
        const response = await endpoint('storeManager.uploadProductImage', { storeGuid, barcode: gtin }).post<{ file: string }>(formData, {
            params: { rotation },
        });
        updateProduct(storeGuid, { gtin, imageUrl: response.file });
    } catch (e) {
        console.error(`Could not upload product ${gtin} image`, e);
    }
}

export async function postUnknownProduct({ gtin, productName, mhdGroup }: { gtin: string; productName: string; mhdGroup?: string }) {
    try {
        const storeGuid = urlService.state.get().params.storeGuid ?? '';
        await endpoint('storeManager.uploadUnknownProduct', { storeGuid, barcode: gtin }).post({ name: productName, mhdGroup });
        updateProduct(storeGuid, { gtin, name: productName, mhdGroup });
    } catch {
        uploadErrorStore.set('Update nicht möglich.');
        setTimeout(() => uploadErrorStore.set(false), 3500);
    }
}
